// A-0008.03: Per-step settings editor — surfaces V2+ fields the engine acts on.
//   timeout_ms : per-step override (otherwise opcode default_step_timeout_ms applies)
//   on_error   : action policy (continue / fail / fallback) + optional fallback_step_id
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    JsonOpcodeStep, OnErrorPolicy
} from 'builders/builderOpcode/jsonMode/types';

const StepSettingsEditor = ({
    step,
    allStepIds,
    onChange,
}: {
    step: JsonOpcodeStep;
    allStepIds: string[];
    onChange: (patch: Partial<JsonOpcodeStep>) => void;
}) => {
    const onError: OnErrorPolicy | undefined = step.on_error;
    const action = onError?.action || 'fail';
    const fallbackId = onError?.fallback_step_id || '';

    const setOnErrorAction = (next: 'continue' | 'fail' | 'fallback') => {
        if (next === 'fail') {
            // 'fail' is the default — clear policy entirely.
            onChange({
 on_error: undefined
});
            return;
        }
        const updated: OnErrorPolicy = {
            action: next,
            ...(next === 'fallback' && fallbackId ? {
 fallback_step_id: fallbackId
} : {}),
        };
        onChange({
 on_error: updated
});
    };

    const setFallbackId = (id: string) => {
        onChange({
            on_error: {
                action: 'fallback',
                fallback_step_id: id || undefined,
            },
        });
    };

    return (
        <div className="border rounded-md p-4 bg-card space-y-4">
            <div>
                <h3 className="text-sm font-semibold mb-1">Step Settings</h3>
                <p className="text-xs text-muted-foreground">
                    V2+ engine controls. <code>timeout_ms</code> overrides the opcode default; <code>on_error</code> chooses how the engine reacts to a step failure.
                </p>
            </div>

            <div>
                <Label className="text-xs">timeout_ms <span className="text-muted-foreground">(optional override)</span></Label>
                <Input
                    type="number"
                    min={0}
                    value={typeof step.timeout_ms === 'number' ? step.timeout_ms : ''}
                    onChange={(e) => {
                        const v = e.target.value.trim();
                        onChange({
                            timeout_ms: v === '' ? undefined : Math.max(0, parseInt(v, 10) || 0),
                        });
                    }}
                    placeholder="e.g. 30000"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">on_error.action</Label>
                    <Select value={action} onValueChange={(v) => setOnErrorAction(v as 'continue' | 'fail' | 'fallback')}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="fail">fail (default — abort opcode)</SelectItem>
                            <SelectItem value="continue">continue (skip step, keep going)</SelectItem>
                            <SelectItem value="fallback">fallback (jump to another step)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {action === 'fallback' && (
                    <div>
                        <Label className="text-xs">on_error.fallback_step_id</Label>
                        <Select value={fallbackId} onValueChange={setFallbackId}>
                            <SelectTrigger><SelectValue placeholder="pick step..." /></SelectTrigger>
                            <SelectContent>
                                {allStepIds.filter(id => id !== step.id).map(id => (
                                    <SelectItem key={id} value={id}>{id}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepSettingsEditor;
